import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { COINS } from "../../utils/constants";
import { formatINR, formatDate, timeAgo } from "../../utils/helpers";
import { downloadPDF } from "../../utils/pdf";
import {
  DollarSign, TrendingUp, TrendingDown, Users, Bitcoin, Wallet, Receipt,
  CreditCard, ArrowUpRight, ArrowDownRight, PieChart, Download, RefreshCw
} from "lucide-react";

function SC({ icon, label, value, sub, color }) {
  const colors = { gold:"#F5A623", blue:"#3B82F6", green:"#10B981", red:"#EF4444", purple:"#8B5CF6", orange:"#F97316" };
  const c = colors[color] || "#F5A623";
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ width:44, height:44, borderRadius:12, background:c+"18", display:"flex", alignItems:"center", justifyContent:"center", color:c, marginBottom:14 }}>{icon}</div>
      <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:1.2, color:"#64748B", marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:800, color:"#E2E8F0", fontFamily:"monospace" }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:"#64748B", marginTop:4 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { expenses, team, cryptoHoldings, auditLog, payments, mode , currentUserId, currentUser, loanRequests, repayLoan} = useApp();

  const isAdmin = mode === "admin";
// Match employee by any possible field
  const myMember = !isAdmin && team ? team.find(m => 
    m.code === currentUserId || 
    m.employeeCode === currentUserId || 
    m.id === currentUserId ||
    (m.name && m.name.toUpperCase() === (currentUser || "").toUpperCase())
  ) : null;
  const mySalary = myMember ? Number(myMember.salary) || 0 : 0;
  
  // Match expenses by userId OR if no userId, show all expenses for employee mode
  // Employee sees ONLY their own expenses, NOT company expenses
  const myExpenses = !isAdmin ? expenses.filter(e => 
    e.userId === currentUserId || 
    e.addedBy === currentUserId
  ) : expenses;
  const myTotalExpenses = myExpenses.reduce((s, e) => s + Number(e.amount), 0);
  
  // Match salary payments for this employee
  const myPayments = !isAdmin && payments ? payments.filter(p =>
    p.employeeId === currentUserId ||
    p.memberId === currentUserId ||
    (myMember && (p.employee === myMember.name || p.name === myMember.name))
  ) : [];
  const myTotalPaid = myPayments.reduce((s, p) => s + Number(p.amount), 0);
  
  // Approved loans for this employee
  const myLoans = !isAdmin && loanRequests ? loanRequests.filter(l => 
    l.status === "Approved" && (
      l.employeeId === currentUserId ||
      l.requestedBy === currentUserId ||
      (myMember && l.employee === myMember.name)
    )
  ) : [];
  const myLoanAmount = myLoans.reduce((s, l) => s + Number(l.amount), 0);
  // Pending loan = approved but not repaid
  const myPendingLoans = myLoans.filter(l => l.repaid !== true);
  const myPendingLoanAmt = myPendingLoans.reduce((s, l) => s + Number(l.amount), 0);
  
  // Employee-specific data
  
    
  

  const [livePrices, setLivePrices] = useState({});
  const [priceLoading, setPriceLoading] = useState(true);

  // Fetch live prices for crypto value calculation
  const fetchPrices = useCallback(async () => {
    setPriceLoading(true);
    try {
      const ids = COINS.filter(c => !c.isFiat).map(c => c.id).join(",");
      const res = await fetch("https://api.coingecko.com/api/v3/simple/price?ids=" + ids + "&vs_currencies=inr");
      const data = await res.json();
      setLivePrices(data);
    } catch (e) { console.error("Price fetch:", e); }
    setPriceLoading(false);
  }, []);

  useEffect(() => { fetchPrices(); const iv = setInterval(fetchPrices, 120000); return () => clearInterval(iv); }, [fetchPrices]);

  // ===== FINANCIAL CALCULATIONS =====
  const today = new Date().toISOString().split("T")[0];
  const monthStart = today.slice(0, 8) + "01";

  // Holdings Value (live crypto + fiat)
  const holdingsValue = cryptoHoldings.reduce((sum, h) => {
    if (h.symbol === "INR") return sum + (Number(h.amount) || 0);
    const coin = COINS.find(c => c.symbol === h.symbol);
    if (coin && livePrices[coin.id]) return sum + (h.amount * (livePrices[coin.id].inr || 0));
    return sum + (h.amount * (h.priceAtReceipt || 0)); // fallback to receipt price
  }, 0);

  // Expenses
  const todayExpenses = expenses.filter(e => e.date === today).reduce((s, e) => s + Number(e.amount), 0);
  const monthExpenses = expenses.filter(e => e.date >= monthStart).reduce((s, e) => s + Number(e.amount), 0);
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount), 0);

  // Salary (from payments)
  const totalSalary = (payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const monthSalary = (payments || []).filter(p => p.date >= monthStart).reduce((s, p) => s + Number(p.amount || 0), 0);

  // Team payroll (monthly commitment)
  const activeTeam = team.filter(m => m.status === "Active");
  const monthlyPayroll = activeTeam.reduce((s, m) => s + Number(m.salary || 0), 0);

  // NET BALANCE
  const netBalance = holdingsValue - totalExpenses - totalSalary;
  const monthNet = holdingsValue - monthExpenses - monthSalary;

  // Expense by category (for chart)
  const categoryMap = {};
  expenses.forEach(e => {
    const cat = e.category || "Other";
    categoryMap[cat] = (categoryMap[cat] || 0) + Number(e.amount);
  });
  const categories = Object.entries(categoryMap).sort((a, b) => b[1] - a[1]);
  const catTotal = categories.reduce((s, c) => s + c[1], 0);

  // 7-day expense trend
  const trendData = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const ds = d.toISOString().split("T")[0];
    const amt = expenses.filter(e => e.date === ds).reduce((s, e) => s + Number(e.amount), 0);
    trendData.push({ date: ds, amount: amt, label: d.toLocaleDateString("en-IN", { day:"numeric", month:"short" }) });
  }
  const maxTrend = Math.max(...trendData.map(d => d.amount), 1);

  // Recent activity
  const recentAudit = [...auditLog].reverse().slice(0, 8);

  // Cat colors
  const catColors = ["#F5A623","#3B82F6","#10B981","#8B5CF6","#EF4444","#F97316","#EC4899","#14B8A6","#6366F1","#84CC16"];

  // PDF Export
  const exportDashboardPDF = () => {
    downloadPDF("Dashboard Summary", ["Metric", "Value"], [
      ["Total Holdings (Live)", formatINR(holdingsValue)],
      ["Total Expenses", formatINR(totalExpenses)],
      ["Total Salary Paid", formatINR(totalSalary)],
      ["Net Balance", formatINR(netBalance)],
      ["Today's Expenses", formatINR(todayExpenses)],
      ["Monthly Expenses", formatINR(monthExpenses)],
      ["Monthly Salary", formatINR(monthSalary)],
      ["Monthly Payroll Commitment", formatINR(monthlyPayroll)],
      ["Active Team Members", String(activeTeam.length)],
      ["Crypto Holdings", String(cryptoHoldings.length) + " assets"],
      ["Total Audit Events", String(auditLog.length)],
    ], [
      { label: "Net Balance", value: formatINR(netBalance), color: netBalance >= 0 ? "#10B981" : "#EF4444" },
      { label: "Holdings", value: formatINR(holdingsValue), color: "#F5A623" },
      { label: "Expenses", value: formatINR(totalExpenses), color: "#EF4444" },
      { label: "Salary Paid", value: formatINR(totalSalary), color: "#3B82F6" },
    ]);
  };


  if (!isAdmin) {
    return (
      <div className="page-content">
        <div className="page-header">
          <div><h3>My Dashboard</h3><span className="text-muted">{myMember ? myMember.name + " | " + myMember.role : "Employee View"}</span></div>
          <button className="btn-ghost" onClick={() => downloadPDF("My Financial Report",
            ["Metric", "Amount"],
            [
              ["Monthly Salary", formatINR(mySalary)],
              ["Salary Received", formatINR(myTotalPaid)],
              ["Advance Loan", formatINR(myLoanAmount)],
              ["Pending Loan", formatINR(myPendingLoanAmt)],
              ["My Expenses", formatINR(myTotalExpenses)],
              ["Total Balance", formatINR(mySalary + myLoanAmount - myTotalExpenses)],
              ["Next Salary", formatINR(mySalary - myPendingLoanAmt)],
            ],
            [
              { label: "Balance", value: formatINR(mySalary + myLoanAmount - myTotalExpenses), color: "#10B981" },
              { label: "Next Salary", value: formatINR(mySalary - myPendingLoanAmt), color: "#6366F1" },
            ]
          )} style={{display:"flex",alignItems:"center",gap:6}}><Download size={14}/> PDF</button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:16,marginBottom:24}}>
          <div className="card" style={{padding:24,background:"linear-gradient(135deg,rgba(59,130,246,.12),rgba(59,130,246,.04))",border:"1px solid rgba(59,130,246,.2)"}}>
            <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>MY SALARY</div>
            <div style={{fontSize:32,fontWeight:800,color:"#3B82F6",fontFamily:"monospace"}}>{formatINR(mySalary)}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:4}}>{myMember ? (myMember.salaryCycle || "Monthly") + " | " + myMember.role : "Monthly"}</div>
          </div>

          <div className="card" style={{padding:24,background:"linear-gradient(135deg,rgba(239,68,68,.12),rgba(239,68,68,.04))",border:"1px solid rgba(239,68,68,.2)"}}>
            <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>MY EXPENSES</div>
            <div style={{fontSize:32,fontWeight:800,color:"#EF4444",fontFamily:"monospace"}}>{formatINR(myTotalExpenses)}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:4}}>{myExpenses.length} transactions</div>
          </div>

          <div className="card" style={{padding:24,background:"linear-gradient(135deg,rgba(16,185,129,.12),rgba(16,185,129,.04))",border:"1px solid rgba(16,185,129,.2)"}}>
            <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>SALARY RECEIVED</div>
            <div style={{fontSize:32,fontWeight:800,color:"#10B981",fontFamily:"monospace"}}>{formatINR(myTotalPaid)}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:4}}>{myPayments.length} payment(s) this cycle</div>
          </div>

          <div className="card" style={{padding:24,background:"linear-gradient(135deg,rgba(245,166,35,.12),rgba(245,166,35,.04))",border:"1px solid rgba(245,166,35,.2)"}}>
            <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>ADVANCE LOAN</div>
            <div style={{fontSize:32,fontWeight:800,color:"#F5A623",fontFamily:"monospace"}}>{formatINR(myPendingLoanAmt)}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:4}}>{myPendingLoans.length} pending repayment</div>
          </div>

          <div className="card" style={{padding:24,background:"linear-gradient(135deg,rgba(139,92,246,.12),rgba(139,92,246,.04))",border:"1px solid rgba(139,92,246,.2)"}}>
            <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>TOTAL BALANCE</div>
            <div style={{fontSize:32,fontWeight:800,color:(myTotalPaid+myLoanAmount-myTotalExpenses)>=0?"#10B981":"#EF4444",fontFamily:"monospace"}}>{formatINR(mySalary + myLoanAmount - myTotalExpenses)}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:4}}>Salary + Loan - Expenses</div>
          </div>

          <div className="card" style={{padding:24,background:"linear-gradient(135deg,rgba(99,102,241,.12),rgba(99,102,241,.04))",border:"1px solid rgba(99,102,241,.2)"}}>
            <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>NEXT SALARY</div>
            <div style={{fontSize:32,fontWeight:800,color:mySalary-myPendingLoanAmt>0?"#6366F1":"#EF4444",fontFamily:"monospace"}}>{formatINR(mySalary - myPendingLoanAmt)}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:4}}>{myPendingLoanAmt > 0 ? "Salary " + formatINR(mySalary) + " - Loan " + formatINR(myPendingLoanAmt) : "No pending loans"}</div>
          </div>
        </div>

        {myExpenses.length > 0 ? (
          <div className="card" style={{padding:24}}>
            <h4 style={{fontSize:15,fontWeight:700,color:"#E2E8F0",marginBottom:16}}>My Recent Expenses</h4>
            <div style={{overflowX:"auto"}}>
              <table><thead><tr><th>Date</th><th>Category</th><th>Description</th><th style={{textAlign:"right"}}>Amount</th><th>Payment</th></tr></thead>
              <tbody>
                {myExpenses.slice(-10).reverse().map(e => (
                  <tr key={e.id}>
                    <td style={{fontSize:12,color:"#94A3B8"}}>{e.date}</td>
                    <td style={{fontSize:12,color:"#CBD5E1"}}>{e.category}</td>
                    <td style={{fontSize:12,color:"#94A3B8"}}>{e.description || "\u2014"}</td>
                    <td style={{textAlign:"right",fontFamily:"monospace",color:"#EF4444",fontWeight:600}}>{formatINR(Number(e.amount))}</td>
                    <td style={{fontSize:11,color:"#64748B"}}>{e.paymentMethod || "\u2014"}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        ) : (
          <div className="card" style={{padding:40,textAlign:"center"}}>
            <div style={{fontSize:14,color:"#64748B"}}>No expenses recorded yet.</div>
          </div>
        )}

        {myLoans.length > 0 && (
          <div className="card" style={{padding:24,marginTop:16}}>
            <h4 style={{fontSize:15,fontWeight:700,color:"#E2E8F0",marginBottom:16}}>My Approved Loans</h4>
            <div style={{overflowX:"auto"}}>
              <table><thead><tr><th>Amount</th><th>Reason</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
              <tbody>
                {myLoans.map((l, i) => (
                  <tr key={i}>
                    <td style={{fontFamily:"monospace",color:"#F5A623",fontWeight:600}}>{formatINR(Number(l.amount))}</td>
                    <td style={{fontSize:12,color:"#CBD5E1"}}>{l.reason || "\u2014"}</td>
                    <td>{l.repaid ? <span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:"rgba(16,185,129,.15)",color:"#10B981"}}>Paid</span> : <span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:"rgba(245,166,35,.15)",color:"#F5A623"}}>Pending</span>}</td>
                    <td style={{fontSize:12,color:"#64748B"}}>{l.createdAt ? new Date(l.createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "\u2014"}</td>
                    <td>{!l.repaid ? <button onClick={() => repayLoan(l.id)} style={{padding:"6px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#10B981,#059669)",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>Pay Now</button> : <span style={{fontSize:12,color:"#10B981",fontWeight:600}}>\u2714 Paid</span>}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        )}
      </div>
    );
  }


  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h3>Dashboard</h3>
          <span className="text-muted">{priceLoading ? "Fetching live prices..." : "Live prices updated"}</span>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="btn-ghost" onClick={fetchPrices} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <RefreshCw size={14} className={priceLoading?"spin":""} /> Refresh
          </button>
          <button className="btn-gold" onClick={exportDashboardPDF} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <Download size={14} /> PDF
          </button>
        </div>
      </div>

      {/* NET BALANCE — Hero Card */}
      <div className="card" style={{ padding:24, marginBottom:24, background:"linear-gradient(135deg, rgba(245,166,35,.08), rgba(59,130,246,.06))", border:"1px solid rgba(245,166,35,.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:1.2, color:"#64748B", marginBottom:6 }}>
              <Wallet size={14} style={{ display:"inline", verticalAlign:"middle", marginRight:6 }} />Net Balance
            </div>
            <div style={{ fontSize:36, fontWeight:900, color: netBalance >= 0 ? "#10B981" : "#EF4444", fontFamily:"monospace" }}>
              {formatINR(netBalance)}
            </div>
            <div style={{ fontSize:12, color:"#64748B", marginTop:6 }}>
              Holdings {formatINR(holdingsValue)} − Expenses {formatINR(totalExpenses)} − Salary {formatINR(totalSalary)}
            </div>
          </div>
          <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:10, color:"#64748B", fontWeight:600 }}>HOLDINGS</div>
              <div style={{ fontSize:20, fontWeight:800, color:"#F5A623", fontFamily:"monospace" }}>{formatINR(holdingsValue)}</div>
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:10, color:"#64748B", fontWeight:600 }}>SPENT</div>
              <div style={{ fontSize:20, fontWeight:800, color:"#EF4444", fontFamily:"monospace" }}>{formatINR(totalExpenses + totalSalary)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="stat-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:16, marginBottom:24 }}>
        <SC icon={<Wallet size={22}/>} label="Total Holdings" value={formatINR(holdingsValue)} sub={cryptoHoldings.length + " assets (live)"} color="gold" />
        <SC icon={<Receipt size={22}/>} label="Today's Expenses" value={formatINR(todayExpenses)} color="red" />
        <SC icon={<TrendingDown size={22}/>} label="Monthly Expenses" value={formatINR(monthExpenses)} color="orange" />
        <SC icon={<CreditCard size={22}/>} label="Salary Paid" value={formatINR(totalSalary)} sub={"This month: " + formatINR(monthSalary)} color="blue" />
        <SC icon={<Users size={22}/>} label="Team Payroll" value={formatINR(monthlyPayroll) + "/mo"} sub={activeTeam.length + " active members"} color="purple" />
        <SC icon={<Bitcoin size={22}/>} label="Crypto Holdings" value={cryptoHoldings.length + " Assets"} color="green" />
      </div>

      {/* CHARTS ROW */}
      <div className="chart-grid" style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:24 }}>
        {/* Expense Trend */}
        <div className="card" style={{ padding:20 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#E2E8F0", marginBottom:16 }}>Expense Trend (7 Days)</h4>
          <div style={{ display:"flex", alignItems:"flex-end", gap:2, height:180 }}>
            {trendData.map((d, i) => (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", height:"100%" }}>
                <div style={{ fontSize:9, color:"#64748B", marginBottom:4 }}>{d.amount > 0 ? formatINR(d.amount) : ""}</div>
                <div style={{ width:"70%", background: d.amount > 0 ? "linear-gradient(to top, #F5A623, #F7C948)" : "rgba(255,255,255,.05)", borderRadius:"4px 4px 0 0", height: Math.max((d.amount / maxTrend) * 140, 4), transition:"height .3s" }} />
                <div style={{ fontSize:10, color:"#64748B", marginTop:6 }}>{d.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="card" style={{ padding:20 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#E2E8F0", marginBottom:16 }}>Category Breakdown</h4>
          {categories.length > 0 ? (
            <div>
              {categories.slice(0, 6).map(([cat, amt], i) => (
                <div key={cat} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                    <span style={{ color:"#CBD5E1" }}>{cat}</span>
                    <span style={{ color:"#94A3B8", fontFamily:"monospace" }}>{formatINR(amt)}</span>
                  </div>
                  <div style={{ height:6, borderRadius:3, background:"rgba(255,255,255,.06)" }}>
                    <div style={{ height:6, borderRadius:3, background:catColors[i % catColors.length], width: (amt/catTotal*100)+"%", transition:"width .3s" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <div style={{ color:"#475569", fontSize:13, textAlign:"center", padding:30 }}>No expense data yet</div>}
        </div>
      </div>

      {/* FINANCIAL FLOW */}
      <div className="card" style={{ padding:20, marginBottom:24 }}>
        <h4 style={{ fontSize:14, fontWeight:700, color:"#E2E8F0", marginBottom:16 }}>
          <PieChart size={16} style={{ display:"inline", verticalAlign:"middle", marginRight:8 }} />Financial Flow
        </h4>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:16 }}>
          <div style={{ padding:16, borderRadius:10, background:"rgba(245,166,35,.06)", border:"1px solid rgba(245,166,35,.15)" }}>
            <div style={{ fontSize:10, color:"#F5A623", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>
              <ArrowUpRight size={12} style={{display:"inline",verticalAlign:"middle"}} /> Income (Holdings)
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:"#F5A623", fontFamily:"monospace" }}>{formatINR(holdingsValue)}</div>
          </div>
          <div style={{ padding:16, borderRadius:10, background:"rgba(239,68,68,.06)", border:"1px solid rgba(239,68,68,.15)" }}>
            <div style={{ fontSize:10, color:"#EF4444", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>
              <ArrowDownRight size={12} style={{display:"inline",verticalAlign:"middle"}} /> Expenses
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:"#EF4444", fontFamily:"monospace" }}>{formatINR(totalExpenses)}</div>
          </div>
          <div style={{ padding:16, borderRadius:10, background:"rgba(59,130,246,.06)", border:"1px solid rgba(59,130,246,.15)" }}>
            <div style={{ fontSize:10, color:"#3B82F6", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>
              <ArrowDownRight size={12} style={{display:"inline",verticalAlign:"middle"}} /> Salary Paid
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:"#3B82F6", fontFamily:"monospace" }}>{formatINR(totalSalary)}</div>
          </div>
          <div style={{ padding:16, borderRadius:10, background: netBalance >= 0 ? "rgba(16,185,129,.06)" : "rgba(239,68,68,.06)", border: netBalance >= 0 ? "1px solid rgba(16,185,129,.15)" : "1px solid rgba(239,68,68,.15)" }}>
            <div style={{ fontSize:10, color: netBalance >= 0 ? "#10B981" : "#EF4444", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>
              <Wallet size={12} style={{display:"inline",verticalAlign:"middle"}} /> Remaining
            </div>
            <div style={{ fontSize:22, fontWeight:800, color: netBalance >= 0 ? "#10B981" : "#EF4444", fontFamily:"monospace" }}>{formatINR(netBalance)}</div>
          </div>
        </div>
      </div>

      {/* RECENT ACTIVITY */}
      <div className="card" style={{ padding:20 }}>
        <h4 style={{ fontSize:14, fontWeight:700, color:"#E2E8F0", marginBottom:16 }}>Recent Activity</h4>
        {recentAudit.map(e => (
          <div key={e.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,.04)" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background: e.action.includes("FAIL") || e.action.includes("LOCK") ? "#EF4444" : e.action.includes("DELETE") ? "#F97316" : "#10B981" }} />
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:"#CBD5E1" }}><strong style={{ color:"#E2E8F0" }}>{e.actor}</strong> — {e.action.replace(/_/g," ")}</div>
              <div style={{ fontSize:10, color:"#475569" }}>{formatDate(e.timestamp)}, {timeAgo(e.timestamp)}</div>
            </div>
          </div>
        ))}
        {auditLog.length === 0 && <div style={{ color:"#475569", fontSize:13, textAlign:"center", padding:20 }}>No activity yet</div>}
      </div>
    </div>
  );
}
